// Backend/cms/src/pages/cv-review/CvReviewPage.tsx
import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Dialog from '../../components/ui/Dialog';
import Textarea from '../../components/ui/Textarea';
import DataTable, { Column, DataTableFetchParams } from '../../components/ui/DataTable';
import { marketplaceService, CvReviewItem } from '../../services/marketplace.service';
import { useConfirm } from '../../hooks/useConfirm';
import { getErrorMsg } from '../../utils/error';

type CvStatus = 'pending' | 'approved' | 'rejected';
type CvReviewRow = CvReviewItem & { id: string };

const TABS: CvStatus[] = ['pending', 'approved', 'rejected'];

const CvReviewPage = () => {
  const { t } = useTranslation('common');
  const { confirm, ConfirmDialog } = useConfirm();
  const [activeTab, setActiveTab] = useState<CvStatus>('pending');
  const [refreshKey, setRefreshKey] = useState(0);
  const [actionError, setActionError] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');

  const handleFetch = useCallback(async (params: DataTableFetchParams) => {
    const response = await marketplaceService.getCvReviewQueue({
      status: activeTab,
      page: params.page,
      take: params.pageSize,
    });
    return {
      data: response.data.map((item): CvReviewRow => ({ ...item, id: item.verificationId })),
      total: response.meta.itemCount,
      totalPages: response.meta.pageCount,
    };
  }, [activeTab]);

  const handleTabChange = (tab: CvStatus) => {
    setActiveTab(tab);
    setActionError('');
    setRefreshKey((k) => k + 1);
  };

  const handleApprove = async (listingId: string) => {
    if (!await confirm(t('approve_cv'))) return;
    setActionError('');
    try {
      await marketplaceService.approveCv(listingId);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setActionError(getErrorMsg(err));
    }
  };

  const openReject = (listingId: string) => {
    setRejectingId(listingId);
    setRejectReason('');
    setRejectError('');
    setRejectOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      setRejectError(t('cv_rejection_reason') + ' is required');
      return;
    }
    try {
      await marketplaceService.rejectCv(rejectingId!, rejectReason.trim());
      setRejectOpen(false);
      setRejectingId(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setRejectError(getErrorMsg(err));
    }
  };

  const tabLabel = (tab: CvStatus) => {
    if (tab === 'pending') return t('pending_tab');
    if (tab === 'approved') return t('approved_tab');
    return t('rejected_tab');
  };

  const columns: Column<CvReviewRow>[] = [
    {
      key: 'listing',
      header: t('title'),
      render: (item) =>
        item.listing ? (
          <Link
            to={`/listings?listingId=${item.listing.id}`}
            className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            {item.listing.title}
          </Link>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'seller',
      header: t('seller_info'),
      render: (item) => (
        <div className="text-sm">
          <p className="font-medium text-gray-900 dark:text-gray-100">{item.seller?.name ?? '—'}</p>
          <p className="text-gray-500 dark:text-gray-400 text-xs">{item.seller?.email ?? ''}</p>
        </div>
      ),
    },
    {
      key: 'cvUrl',
      header: t('cv_url'),
      render: (item) => (
        <a
          href={item.cvUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          View CV ↗
        </a>
      ),
    },
    {
      key: 'submittedAt',
      header: t('submitted_at'),
      render: (item) => new Date(item.submittedAt).toLocaleDateString(),
    },
    ...(activeTab !== 'pending'
      ? [
          {
            key: 'reviewedAt',
            header: t('reviewed_at'),
            render: (item: CvReviewRow) =>
              item.reviewedAt ? new Date(item.reviewedAt).toLocaleDateString() : '—',
          } as Column<CvReviewRow>,
          ...(activeTab === 'rejected'
            ? [
                {
                  key: 'rejectionReason',
                  header: t('rejection_reason'),
                  render: (item: CvReviewRow) => (
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {item.rejectionReason ?? '—'}
                    </span>
                  ),
                } as Column<CvReviewRow>,
              ]
            : []),
        ]
      : []),
    ...(activeTab === 'pending'
      ? [
          {
            key: 'actions',
            header: t('actions'),
            className: 'text-right',
            render: (item: CvReviewRow) => (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="primary" onClick={() => handleApprove(item.listingId)}>
                  {t('approve_cv')}
                </Button>
                <Button size="sm" variant="danger" onClick={() => openReject(item.listingId)}>
                  {t('reject_cv')}
                </Button>
              </div>
            ),
          } as Column<CvReviewRow>,
        ]
      : []),
  ];

  return (
    <Layout title={t('cv_review')}>
      <div className="space-y-6 h-full flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <h1 className="text-2xl font-semibold tracking-tight">{t('cv_review')}</h1>
          {actionError && (
            <div className="mt-2 p-2 rounded bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm border border-red-100 dark:border-red-800">
              {actionError}
            </div>
          )}
        </Card>

        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={[
                'px-4 py-2 text-sm font-medium transition-colors',
                activeTab === tab
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
              ].join(' ')}
            >
              {tabLabel(tab)}
            </button>
          ))}
        </div>

        <DataTable
          className="flex-1 min-h-0"
          columns={columns}
          onFetch={handleFetch}
          refreshKey={refreshKey}
          initialState={{ sortBy: 'submittedAt', sortOrder: 'DESC', pageSize: 20 }}
          emptyMessage={activeTab === 'pending' ? t('no_pending_cvs') : t('no_cvs')}
        />

        <Dialog
          open={rejectOpen}
          onClose={() => setRejectOpen(false)}
          title={t('reject_cv')}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">{t('reject_cv_prompt')}</p>
            <Textarea
              label={t('cv_rejection_reason')}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Provide a reason..."
              error={rejectError}
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setRejectOpen(false)}>{t('cancel')}</Button>
              <Button variant="danger" onClick={handleRejectConfirm}>{t('reject_cv')}</Button>
            </div>
          </div>
        </Dialog>
      </div>
      {ConfirmDialog}
    </Layout>
  );
};

export default CvReviewPage;
